import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { I18nContext, en, langModules, type LangCode } from './index';
import { loadLang, STORAGE_KEY } from './loadLang';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [lang, setLangState] = useState<LangCode>(loadLang);
  const strings = useMemo(() => (lang === 'en' ? en : langModules[lang] ?? en), [lang]);
  const didMountRef = useRef(false);

  const setLang = useCallback((code: LangCode) => {
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // ignore storage failures (private mode, quota etc.)
    }
  }, []);

  // Re-fetch all server-sourced data when the user switches language so
  // localized product data, master data etc. reflect the new locale. Skip on
  // the initial render (lang is already correct when the first fetch happens).
  // Skip when offline: invalidating would trigger refetches that fail and
  // could overwrite the cached data the user still relies on offline.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }
    queryClient.invalidateQueries();
  }, [lang, queryClient]);

  const value = useMemo(() => ({ t: strings, lang, setLang }), [strings, lang, setLang]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};
